import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, Copy, FileText, RefreshCw, Search, ThumbsDown, ThumbsUp } from 'lucide-react';
import type { CoachMessage } from '../../../api/types';
import { withTime } from '../../../lib/dates';
import { markdownToText } from '../../../lib/markdownLite';
import CoachActionCard from './CoachActionCard';
import CoachMarkdown from './CoachMarkdown';
import { coachHref } from './coachLinks';

interface Props {
  messages: CoachMessage[];
  pending: boolean;
  streamingText?: string;
  /** What the coach is looking up right now ("Opening an exam"). */
  toolLabel?: string | null;
  /** A stream error not yet saved as a message (e.g. the connection failed). */
  streamError?: string | null;
  /** The question being answered, shown before it is saved. */
  pendingQuestion?: string | null;
  hasOlder?: boolean;
  loadingOlder?: boolean;
  onLoadOlder?: () => void;
  onRegenerate?: (messageId: number) => void;
  onAsk?: (text: string) => void;
  onFeedback?: (messageId: number, value: -1 | 0 | 1, note?: string) => void;
}

const ICON = 'inline-grid h-8 w-8 place-items-center rounded-[9px] text-muted hover:bg-surface-2 hover:text-ink disabled:opacity-40';

function Answer({ m, last, pending, onRegenerate, onAsk, onFeedback }: {
  m: CoachMessage; last: boolean; pending: boolean;
  onRegenerate?: Props['onRegenerate']; onAsk?: Props['onAsk']; onFeedback?: Props['onFeedback'];
}) {
  const [copied, setCopied] = useState(false);
  const [noteOpen, setNoteOpen] = useState(false);
  const [note, setNote] = useState(m.feedback_note ?? '');
  const [dismissed, setDismissed] = useState<number[]>([]);
  const failed = m.status === 'error';
  return (
    <div className="mr-auto w-full max-w-[94%] space-y-2" title={withTime(m.created_at)}>
      {failed ? (
        <div data-testid="coach-error" role="alert"
          className="rounded-[14px] border border-danger-line bg-danger-bg px-3 py-2 text-sm text-danger-ink">
          The coach couldn&apos;t answer.{' '}
          {last && onRegenerate && <button type="button" onClick={() => onRegenerate(m.id)} disabled={pending} className="font-semibold underline">Try again</button>}
          {m.content && <div className="mt-1 text-muted-2"><CoachMarkdown text={m.content} /></div>}
        </div>
      ) : (
        <div className="rounded-[16px] rounded-tl-[6px] bg-surface-2 px-3.5 py-2.5">
          <CoachMarkdown text={m.content} />
          {m.status === 'interrupted' && <p className="mt-1 text-xs italic text-muted">Stopped.</p>}
        </div>
      )}

      {!failed && (m.sources?.length ?? 0) > 0 && (
        <div className="flex flex-wrap items-center gap-1.5" aria-label="Sources">
          <span className="text-[11px] font-semibold uppercase tracking-[0.06em] text-muted">Sources</span>
          {m.sources!.map((s, i) => {
            const href = coachHref(s.kind, s.ref);
            const chip = 'inline-flex max-w-[240px] items-center gap-1 truncate rounded-full border border-line px-2.5 py-0.5 text-xs text-ink';
            return href
              ? <Link key={i} to={href} className={`${chip} hover:bg-surface-2`}><FileText size={12} aria-hidden /> <span className="truncate">{s.label}</span></Link>
              : <span key={i} className={chip}>{s.label}</span>;
          })}
        </div>
      )}

      {!failed && (
        <div className="flex items-center gap-0.5" aria-label="Answer tools">
          <button type="button" className={ICON} aria-label="Copy answer" title="Copy"
            onClick={async () => {
              try { await navigator.clipboard.writeText(markdownToText(m.content)); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch { /* blocked */ }
            }}>{copied ? <Check size={16} aria-hidden /> : <Copy size={16} aria-hidden />}</button>
          {last && onRegenerate && (
            <button type="button" className={ICON} aria-label="Regenerate answer" title="Regenerate" disabled={pending}
              onClick={() => onRegenerate(m.id)}><RefreshCw size={16} aria-hidden /></button>
          )}
          {onFeedback && (<>
            <button type="button" className={`${ICON} ${m.feedback === 1 ? '!text-success' : ''}`} aria-pressed={m.feedback === 1}
              aria-label="Helpful" title="Helpful" onClick={() => { onFeedback(m.id, m.feedback === 1 ? 0 : 1); setNoteOpen(false); }}>
              <ThumbsUp size={16} aria-hidden /></button>
            <button type="button" className={`${ICON} ${m.feedback === -1 ? '!text-danger-ink' : ''}`} aria-pressed={m.feedback === -1}
              aria-label="Not helpful" title="Not helpful"
              onClick={() => { if (m.feedback === -1) { onFeedback(m.id, 0); setNoteOpen(false); } else { onFeedback(m.id, -1); setNoteOpen(true); } }}>
              <ThumbsDown size={16} aria-hidden /></button>
          </>)}
        </div>
      )}
      {noteOpen && onFeedback && (
        <form className="flex gap-2" onSubmit={(e) => { e.preventDefault(); onFeedback(m.id, -1, note.trim()); setNoteOpen(false); }}>
          <input value={note} onChange={(e) => setNote(e.target.value.slice(0, 500))} aria-label="What was wrong?"
            placeholder="What was wrong? (optional)"
            className="min-h-[36px] flex-1 rounded-[10px] border border-line bg-surface px-2.5 text-[13px] text-ink" />
          <button type="submit" className="rounded-[10px] border border-line-strong px-3 text-[13px] font-semibold text-ink">Send</button>
        </form>
      )}

      {!failed && last && (m.actions ?? []).map((a, i) => dismissed.includes(i) ? null : (
        <CoachActionCard key={i} action={a} onDismiss={() => setDismissed((d) => [...d, i])} />
      ))}

      {!failed && last && !pending && (m.suggestions?.length ?? 0) > 0 && onAsk && (
        <div className="flex flex-col items-start gap-1.5" aria-label="Suggested follow-ups">
          {m.suggestions!.map((q, i) => (
            <button key={i} type="button" onClick={() => onAsk(q)}
              className="rounded-full border border-accent-line bg-accent-bg px-3 py-1 text-left text-[13px] text-accent-ink hover:brightness-95">
              {q}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function CoachMessageList({
  messages, pending, streamingText, toolLabel, streamError, pendingQuestion, hasOlder, loadingOlder, onLoadOlder,
  onRegenerate, onAsk, onFeedback,
}: Props) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (ref.current) ref.current.scrollTop = ref.current.scrollHeight;
  }, [messages.length, pending, streamingText, toolLabel, streamError, pendingQuestion]);

  const lastAssistant = [...messages].reverse().find((m) => m.role === 'assistant');
  const lastIsAssistant = messages.length > 0 && messages[messages.length - 1].role === 'assistant';
  const showStreaming = !!streamingText;

  return (
    <div ref={ref} className="flex-1 space-y-4 overflow-y-auto px-4 py-3" aria-live="polite">
      {hasOlder && (
        <div className="text-center">
          <button type="button" onClick={onLoadOlder} disabled={loadingOlder}
            className="text-[13px] font-semibold text-slate hover:underline disabled:opacity-50">
            {loadingOlder ? 'Loading…' : 'Load earlier messages'}
          </button>
        </div>
      )}
      {messages.map((m) => m.role === 'user' ? (
        <div key={m.id} title={withTime(m.created_at)}
          className="ml-auto max-w-[85%] whitespace-pre-wrap break-words rounded-[16px] rounded-tr-[6px] bg-navy px-3.5 py-2 text-sm text-white dark:bg-mist-fixed dark:text-navy">
          {m.content}
        </div>
      ) : (
        <Answer key={m.id} m={m} last={lastIsAssistant && m.id === lastAssistant?.id} pending={pending}
          onRegenerate={onRegenerate} onAsk={onAsk} onFeedback={onFeedback} />
      ))}
      {pending && pendingQuestion && messages[messages.length - 1]?.content !== pendingQuestion && (
        <div data-testid="coach-pending-question"
          className="ml-auto max-w-[85%] whitespace-pre-wrap break-words rounded-[16px] rounded-tr-[6px] bg-navy px-3.5 py-2 text-sm text-white dark:bg-mist-fixed dark:text-navy">
          {pendingQuestion}
        </div>
      )}
      {pending && (
        <div className="mr-auto w-full max-w-[94%] space-y-1.5">
          {toolLabel && (
            <p data-testid="coach-tool" className="flex items-center gap-1.5 text-xs text-muted">
              <Search size={13} aria-hidden className="animate-pulse" /> {toolLabel}…
            </p>
          )}
          {showStreaming ? (
            <div data-testid="coach-streaming" className="rounded-[16px] rounded-tl-[6px] bg-surface-2 px-3.5 py-2.5">
              <CoachMarkdown text={streamingText!} streaming />
              <span className="animate-pulse text-muted" aria-hidden>▌</span>
            </div>
          ) : !toolLabel && (
            <p data-testid="coach-pending" className="text-sm italic text-muted">Coach is thinking…</p>
          )}
        </div>
      )}
      {streamError && !pending && (
        <div role="alert" className="mr-auto rounded-[14px] border border-danger-line bg-danger-bg px-3 py-2 text-sm text-danger-ink">
          {streamError}
        </div>
      )}
    </div>
  );
}
